import { apiGet, apiSend } from './client';
import type { UserRole } from './types';

export type BonusActionType =
  | 'ADD'
  | 'REMOVE'
  | 'FLAG_REMOVAL'
  | 'CONFIRM_REMOVAL'
  | 'CANCEL_REMOVAL'
  | 'ADMIN_REMOVE'
  | 'ADMIN_RESTORE'
  | 'REPORT'
  | 'IMPORT';

export type BonusReportStatus = 'OPEN' | 'RESOLVED' | 'DISMISSED';

export interface AdminBonusUserRef {
  id: string;
  username: string;
  role: UserRole;
}

export interface AdminBonusServerRef {
  id: number;
  name: string;
  slug: string;
}

export interface AdminBonusZoneRef {
  id: string;
  name: string;
  slug: string;
  expansion?: string | null;
}

export interface AdminBonusTypeRef {
  id: string;
  key: string;
  label: string;
}

export interface AdminBonusAction {
  id: string;
  serverId: number;
  zoneId?: string | null;
  zoneBonusId?: string | null;
  bonusTypeId?: string | null;
  actorId?: string | null;
  actionType: BonusActionType;
  bonusDate?: string | null;
  reason?: string | null;
  metadata?: unknown;
  createdAt: string;
  server?: AdminBonusServerRef | null;
  actor?: AdminBonusUserRef | null;
  zone?: AdminBonusZoneRef | null;
  bonusType?: AdminBonusTypeRef | null;
  zoneBonus?: {
    id: string;
    state: string;
    bonusDate: string;
    zone?: AdminBonusZoneRef | null;
    bonusType?: AdminBonusTypeRef | null;
  } | null;
}

export interface AdminBonusReport {
  id: string;
  serverId: number;
  actionId: string;
  zoneBonusId?: string | null;
  reporterId: string;
  reason?: string | null;
  moderatorNote?: string | null;
  status: BonusReportStatus;
  resolvedAt?: string | null;
  resolvedById?: string | null;
  createdAt: string;
  updatedAt: string;
  server?: AdminBonusServerRef | null;
  reporter?: AdminBonusUserRef | null;
  action: AdminBonusAction;
}

export interface AdminReportedUser {
  id: string;
  username: string;
  role: UserRole;
  openReportCount: number;
}

export interface AdminBonusModerationSummary {
  reports: AdminBonusReport[];
  recentActions: AdminBonusAction[];
  users: AdminReportedUser[];
}

export interface AdminBonusDiscipline {
  id: string;
  userId: string;
  actorId?: string | null;
  type: 'WARNING' | 'TIMEOUT' | 'BAN';
  reason?: string | null;
  startsAt: string;
  expiresAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
  user?: AdminBonusUserRef | null;
  actor?: AdminBonusUserRef | null;
}

export function fetchAdminBonusModeration() {
  return apiGet<AdminBonusModerationSummary>('/admin/bonuses');
}

export function fetchRecentBonusActions(limit = 100) {
  return apiGet<{ actions: AdminBonusAction[] }>(`/admin/bonuses/actions/recent?limit=${encodeURIComponent(String(limit))}`);
}

export function resolveBonusReport(reportId: string, reason?: string) {
  return apiSend<{ report: AdminBonusReport }>(`/admin/bonuses/reports/${reportId}/resolve`, 'POST', { reason });
}

export function dismissBonusReport(reportId: string, reason?: string) {
  return apiSend<{ report: AdminBonusReport }>(`/admin/bonuses/reports/${reportId}/dismiss`, 'POST', { reason });
}

export function adminRemoveBonus(bonusId: string, reason?: string) {
  return apiSend(`/admin/bonuses/bonuses/${bonusId}/remove`, 'POST', { reason });
}

export function adminRestoreBonus(bonusId: string, reason?: string) {
  return apiSend(`/admin/bonuses/bonuses/${bonusId}/restore`, 'POST', { reason });
}

export function timeoutBonusUser(userId: string, days: number, reason?: string) {
  return apiSend<{ discipline: AdminBonusDiscipline }>(`/admin/bonuses/users/${userId}/timeout`, 'POST', { days, reason });
}

export function banBonusUser(userId: string, reason?: string) {
  return apiSend<{ discipline: AdminBonusDiscipline }>(`/admin/bonuses/users/${userId}/ban`, 'POST', { reason });
}
